<template>
  <div class="career-list">
    <div class="flex gap-5 justify-between items-end mb-6 mdmax:flex-col">
      <div class="flex w-full gap-5 mdmax:w-full mdmax:flex-col">
        <div class="w-60 mdmax:w-full">
          <label
            class="text-base text-neutral-700 font-medium mb-1 inline-block"
            >Work Type</label
          >
          <CustomSelect
            :options="workTypeOptions"
            v-model="workType"
            :default="workType"
            placeholder="All Work Types"
            @change="onFilterChange"
          />
        </div>
      </div>
      <div>
        <button
          @click="$router.push('/career/create')"
          class="px-2 py-2 w-32 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-colors"
        >
          + Add Career
        </button>
      </div>
    </div>
    <div class="bg-white rounded-lg shadow-sm overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr
              class="bg-neutral-50 text-neutral-500 text-sm font-semibold border-b border-neutral-200"
            >
              <th class="p-4">No</th>
              <th class="p-4">Title</th>
              <th class="p-4">Location</th>
              <th class="p-4">Work Type</th>
              <th class="p-4">Start Date</th>
              <th class="p-4">End Date</th>
              <th class="p-4">Action</th>
            </tr>
          </thead>
          <tbody class="text-neutral-700 text-sm">
            <tr
              v-for="(career, index) in items"
              :key="career.id"
              class="border-b border-neutral-100 hover:bg-neutral-50 transition-colors"
            >
              <td class="p-4">{{ (page - 1) * limit + index + 1 }}</td>
              <td class="p-4 font-medium">{{ career.title }}</td>
              <td class="p-4">{{ career.location }}</td>
              <td class="p-4">
                <span
                  class="px-2 py-1 rounded text-xs font-medium"
                  :class="workTypeClass(career.work_type)"
                >
                  {{ career.work_type }}
                </span>
              </td>
              <td class="p-4">{{ formatDate(career.start_date) }}</td>
              <td class="p-4">{{ formatDate(career.end_date) }}</td>
              <td class="p-4">
                <div class="flex items-center gap-3">
                  <button
                    @click="onDetail(career)"
                    class="text-neutral-500 hover:text-blue-500 transition-colors"
                    title="View Details & Applications"
                  >
                    <Eye width="18" height="18" />
                  </button>
                  <button
                    @click="onEdit(career)"
                    class="text-neutral-500 hover:text-primary-500 transition-colors"
                    title="Edit Career"
                  >
                    <Pencil width="18" height="18" />
                  </button>
                  <button
                    @click="onDelete(career)"
                    class="text-neutral-500 hover:text-red-500 transition-colors"
                    title="Delete Career"
                  >
                    <Trash width="18" height="18" />
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="!items || items.length === 0">
              <td colspan="7" class="p-4 text-center text-neutral-500">
                Data tidak ditemukan!
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div
      class="text-neutral-500 font-medium mt-2 text-sm"
      v-if="!isMobile && items.length > 0"
    >
      Menampilkan {{ (page - 1) * limit + 1 }} -
      {{ Math.min(page * limit, meta.total_count) }} dari {{ meta.total_count }}
    </div>
    <div
      class="mt-8 flex justify-end items-center mb-6"
      v-if="items.length > 0"
    >
      <pagination
        :currentPage="page"
        :totalCount="meta.total_count"
        :pageSize="limit"
        @onPageChange="changePage"
      />
    </div>

    <Modal
      :modalVisible="deleteModalVisible"
      @close="closeDeleteModal"
      width="400px"
    >
      <div slot="modal-content" class="text-center">
        <h3 class="text-lg font-bold text-neutral-800 mb-2">Hapus Career</h3>
        <p class="text-neutral-600 mb-6">
          Apakah Anda yakin ingin menghapus career
          <strong>{{ careerToDelete ? careerToDelete.title : "" }}</strong
          >?
        </p>
        <div class="flex justify-center gap-3">
          <button
            @click="closeDeleteModal"
            class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-50 font-medium"
          >
            Batal
          </button>
          <button
            @click="confirmDelete"
            class="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 font-medium"
          >
            Hapus
          </button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
import dayjs from "dayjs"

export default {
  name: "CareerList",
  components: {
    CustomSelect: () => import("@/components/Forms/CustomSelect"),
    Pagination: () => import("@/components/Pagination/Pagination"),
    Eye: () => import("@/components/Icons/Eye"),
    Pencil: () => import("@/components/Icons/Pencil"),
    Trash: () => import("@/components/Icons/Trash"),
    Modal: () => import("@/components/Modal/Modal")
  },
  data: () => ({
    workType: "",
    page: 1,
    limit: 10,
    workTypeOptions: ["Remote", "Hybrid", "Onsite"],
    deleteModalVisible: false,
    careerToDelete: null
  }),
  computed: {
    ...mapGetters("career", ["careers"]),
    ...mapGetters("common", ["isMobile"]),
    meta() {
      return this.careers.data || { total_pages: 1, total_count: 0 }
    },
    items() {
      return this.careers.data?.items || []
    }
  },
  methods: {
    ...mapActions("career", ["getCareerList", "deleteCareer"]),
    ...mapActions("common", ["showLoading", "hideLoading", "showExactToast"]),

    formatDate(date) {
      if (!date) return "-"
      const rawDate = typeof date === "string" ? date.replace("Z", "") : date
      return dayjs(rawDate).format("DD MMM YYYY")
    },
    workTypeClass(workType) {
      if (workType === "Remote") return "bg-blue-100 text-blue-700"
      if (workType === "Hybrid") return "bg-purple-100 text-purple-700"
      if (workType === "Onsite") return "bg-green-100 text-green-700"
      return "bg-gray-100 text-gray-700"
    },
    fetchCareers() {
      this.showLoading()
      const params = {
        page: this.page,
        limit: this.limit
      }
      if (this.workType) {
        params.work_type = this.workType
      }
      this.getCareerList(params)
        .catch((err) => console.error(err))
        .finally(() => this.hideLoading())
    },
    onFilterChange() {
      this.page = 1
      this.fetchCareers()
    },
    changePage(val) {
      this.page = val
      this.fetchCareers()
    },
    onDetail(career) {
      this.$router.push(`/career/detail/${career.id}`)
    },
    onEdit(career) {
      this.$router.push(`/career/${career.id}`)
    },
    onDelete(career) {
      this.careerToDelete = career
      this.deleteModalVisible = true
    },
    closeDeleteModal() {
      this.deleteModalVisible = false
      this.careerToDelete = null
    },
    async confirmDelete() {
      if (!this.careerToDelete) return

      this.showLoading()
      try {
        await this.deleteCareer(this.careerToDelete.id)
        this.showExactToast({
          message: "Career berhasil dihapus",
          type: "success"
        })
        this.fetchCareers()
      } catch (error) {
        console.error(error)
        this.showExactToast({
          message: error.response?.data?.message || "Gagal menghapus career",
          type: "error"
        })
      } finally {
        this.hideLoading()
        this.closeDeleteModal()
      }
    }
  },
  mounted() {
    this.fetchCareers()
  }
}
</script>

<style lang="scss" scoped></style>
